/**
 * Shared TanStack Query client.
 *
 * Defaults are tuned for internal apps that change infrequently:
 *   - staleTime 1 min so navigating between pages doesn't refetch constantly
 *   - no auto-refetch on window focus (more startling than helpful) — except
 *     `me` (see `useMe`), so a returning tab finds out it is signed out early
 *   - no retries on 401/403 — let ProtectedRoute redirect instead — nor on a
 *     request with no answer, which the API client has already retried
 *
 * A 401 from any query or mutation means the session expired mid-use, so the
 * `me` query is re-checked: its 401 is what makes `ProtectedRoute` send the
 * person to sign in, with where they were as the return path. See
 * `@/lib/session-error` for why `me`'s own failure is excluded.
 */

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/logger";
import { shouldRecheckSession, shouldRetryQuery } from "@/lib/session-error";

function recheckSessionOn(error: unknown, queryKey: readonly unknown[] | undefined): void {
  if (!shouldRecheckSession(error, queryKey)) {
    return;
  }
  logger.debug("[auth] 401 from an API call; re-checking the session", queryKey);
  void queryClient.invalidateQueries({ queryKey: ["me"] });
}

export const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => recheckSessionOn(error, query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error) => recheckSessionOn(error, undefined),
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Not on 401/403, and not on "no answer" (status 0): the API client
      // already retried that, backing off (12 Oct).
      retry: (failureCount, error) => shouldRetryQuery(failureCount, error, 2),
    },
    mutations: {
      retry: false,
    },
  },
});
